import React from 'react';
import { Card, CardContent, Box, Typography, Skeleton } from '@mui/material';
import { KPI_PALETTE, TOKEN_COLOR } from './tokens';
import InfoTip from './InfoTip';

export default function KpiCard({
    label,
    value,
    sub,
    icon,
    color = 'blue',
    selected = false,
    highlightValue = false,
    // Mockup highlights certain critical KPIs ("Needs Scheduling", "3+ Misses
    // Escalation") with a 2px solid red border. Use this prop to opt in.
    borderColor,
    onClick,
    // Pulse a placeholder instead of the value while the backing request is in flight.
    loading = false,
    // Optional tooltip text explaining how the value is computed ("i" icon next to the label).
    info,
    // Optional colour for the number (e.g. the category colour); highlightValue still wins.
    valueColor,
}) {
    const c = KPI_PALETTE[color] || KPI_PALETTE.blue;
    const borderStyle = borderColor
        ? `2px solid ${borderColor}`
        : (selected ? `2px solid ${TOKEN_COLOR.primary}` : '2px solid transparent');
    // Sample CSS reference (`.kpi-card`, `.kpi-icon`, `.kpi-label`, `.kpi-value`, `.kpi-sub`):
    //   card:    border-radius 10px, padding 14px 18px, gap 14px
    //   icon:    44×44, border-radius 10px, svg 22px
    //   label:   11px, uppercase, letter-spacing 0.5px, #888, fw 600
    //   value:   28px, fw 800, #1a1a2e, line-height 1.1, mt 2px
    //   sub:     11px, #999
    return (
        <Card
            onClick={onClick}
            sx={{
                cursor: onClick ? 'pointer' : 'default',
                borderRadius: '10px',
                border: borderStyle,
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                transition: 'box-shadow 0.15s, transform 0.15s',
                '&:hover': onClick ? { boxShadow: '0 3px 10px rgba(0,0,0,0.1)', transform: 'translateY(-1px)' } : {},
                height: '100%',
            }}
        >
            <CardContent sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                padding: '14px 18px',
                '&:last-child': { pb: '14px' },
            }}>
                <Box
                    sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '10px',
                        bgcolor: c.bg,
                        color: c.fg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        '& svg': { fontSize: 22 },
                    }}
                >
                    {icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography
                            sx={{
                                fontSize: '11px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                color: '#888',
                                fontWeight: 600,
                                lineHeight: 1.2,
                            }}
                        >
                            {label}
                        </Typography>
                        <InfoTip title={info} />
                    </Box>
                    <Typography
                        sx={{
                            fontSize: '28px',
                            fontWeight: 800,
                            color: highlightValue ? TOKEN_COLOR.danger : (valueColor || TOKEN_COLOR.text),
                            lineHeight: 1.1,
                            mt: '2px',
                        }}
                    >
                        {loading ? <Skeleton variant='text' width={72} /> : (value ?? '—')}
                    </Typography>
                    {sub && (
                        <Typography sx={{ fontSize: '11px', color: '#999', mt: '2px' }}>{sub}</Typography>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
}
